import api from './api';
import { getCached, setCached, CACHE_TTL } from '../utils/cache';

const EMPLOYEES_CACHE_KEY = 'employees_local_api';
const PROJECTS_CACHE_KEY = 'projectLocations_local_api';

const normalizeEmployeeId = (employee) => employee?.id || employee?.employee_id || employee?.employeeId;

const buildImageUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3008').replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const formatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
};

const mapAttendanceDetailToLegacyRecords = (attendance) => {
  if (!attendance) return [];
  const records = [];
  const images = Array.isArray(attendance.images) ? attendance.images : [];
  const checkinImage = images.find((item) => item.type === 'checkin');
  const checkoutImage = images.find((item) => item.type === 'checkout');

  if (attendance.checkin_time) {
    records.push({
      id: `${attendance.id}-checkin`,
      timestamp: attendance.checkin_time,
      date: attendance.date,
      time: formatTime(attendance.checkin_time),
      employee_id: attendance.employee_id,
      employee_code: attendance.employee?.code || '',
      employee_name: attendance.employee?.name || '',
      type: 'Vào',
      latitude: attendance.checkin_latitude,
      longitude: attendance.checkin_longitude,
      address: attendance.checkin_address || '',
      photo_url: buildImageUrl(checkinImage?.path),
      note: attendance.checkin_note || '',
      explanation: attendance.checkin_explanation || '',
      work_status_final: '',
      office_work_report: '',
      outside_office_report: '',
      province_trip_report: '',
      training_event_report: '',
    });
  }

  if (attendance.checkout_time) {
    records.push({
      id: `${attendance.id}-checkout`,
      timestamp: attendance.checkout_time,
      date: attendance.date,
      time: formatTime(attendance.checkout_time),
      employee_id: attendance.employee_id,
      employee_code: attendance.employee?.code || '',
      employee_name: attendance.employee?.name || '',
      type: 'Ra',
      latitude: attendance.checkout_latitude,
      longitude: attendance.checkout_longitude,
      address: attendance.checkout_address || '',
      photo_url: buildImageUrl(checkoutImage?.path),
      note: attendance.checkout_note || '',
      explanation: attendance.checkout_explanation || '',
      work_status_final: attendance.work_status_final || '',
      office_work_report: attendance.office_work_report || '',
      outside_office_report: attendance.outside_office_report || '',
      province_trip_report: attendance.province_trip_report || '',
      training_event_report: attendance.training_event_report || '',
    });
  }

  return records;
};

const extractApiError = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) {
    return { ok: false, error: detail };
  }
  if (detail && typeof detail === 'object') {
    return {
      ok: false,
      error: detail.message || fallbackMessage,
      note: detail.note || '',
      requiresExplanation: !!detail.requiresExplanation,
    };
  }
  return { ok: false, error: error?.message || fallbackMessage };
};

export const getEmployees = async (forceRefresh = false) => {
  if (!forceRefresh) {
    const cached = getCached(EMPLOYEES_CACHE_KEY, CACHE_TTL.EMPLOYEES);
    if (cached) return cached;
  }
  const data = await api.get('/api/employees');
  setCached(EMPLOYEES_CACHE_KEY, data);
  return data;
};

export const getProjectLocations = async (forceRefresh = false) => {
  if (!forceRefresh) {
    const cached = getCached(PROJECTS_CACHE_KEY, CACHE_TTL.PROJECT_LOCATIONS);
    if (cached) return cached;
  }
  const data = await api.get('/api/projects/locations');
  setCached(PROJECTS_CACHE_KEY, data);
  return data;
};

export const getTodayAttendance = async (employee, _unused = null, forceRefresh = false) => {
  const employeeId = normalizeEmployeeId(employee);
  if (!employeeId) return null;
  const cacheKey = `today_attendance_${employeeId}`;
  if (!forceRefresh) {
    const cached = getCached(cacheKey, CACHE_TTL.TODAY_ATTENDANCE);
    if (cached !== null && cached !== undefined) return cached;
  }

  try {
    const data = await api.get(`/api/attendance/today/${employeeId}`);
    const mapped = data
      ? {
          checkin_time: data.checkin_time || null,
          checkout_time: data.checkout_time || null,
          work_hours: data.work_hours || 0,
        }
      : null;
    setCached(cacheKey, mapped);
    return mapped;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

export const getAttendanceHistory = async (employee, startDate = null, endDate = null) => {
  const employeeId = normalizeEmployeeId(employee);
  if (!employeeId) return [];
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const data = await api.get(`/api/attendance/employee/${employeeId}`, { params });
  return (Array.isArray(data) ? data : []).flatMap(mapAttendanceDetailToLegacyRecords);
};

export const submitAttendance = async (data) => {
  const formData = new FormData();
  formData.append('employee_id', data.employee_id);
  formData.append('latitude', data.latitude ?? '');
  formData.append('longitude', data.longitude ?? '');
  formData.append('address', data.address || '');
  formData.append('explanation', data.explanation || '');
  formData.append('business_report', data.business_report || '');
  formData.append('request_id', data.request_id || '');
  formData.append('work_status_final', data.work_status_final || '');
  formData.append('office_work_report', data.office_work_report || '');
  formData.append('outside_office_report', data.outside_office_report || '');
  formData.append('province_trip_report', data.province_trip_report || '');
  formData.append('training_event_report', data.training_event_report || '');
  if (data.image) {
    formData.append('image', data.image);
  }

  try {
    const response = await api.post(`/api/attendance/${data.type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const timestamp = data.type === 'checkin' ? response.checkin_time : response.checkout_time;
    return {
      ok: true,
      timestamp,
      used_timestamp_ms: timestamp ? new Date(timestamp).getTime() : Date.now(),
    };
  } catch (error) {
    return extractApiError(error, 'Có lỗi xảy ra khi chấm công');
  }
};
